import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate } from 'react-router-dom';
import { Button,
  Text,
  Icon,
  VStack,
  HStack,
  Box,
  Separator,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { FaEnvelope, FaFileContract, FaReply, FaStar } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { notificacionesAPI } from '../services/api';
import { clearLoginAlertsPending, hasPendingLoginAlerts } from '../utils/loginAlerts';

const SECTIONS = [
  {
    key: 'nuevosChats',
    title: 'Nuevos chats',
    description: 'Bandas o cofradías que han iniciado una conversación contigo.',
    icon: FaEnvelope,
    color: 'purple',
  },
  {
    key: 'propuestasAcuerdos',
    title: 'Propuestas de acuerdo',
    description: 'Solicitudes de acuerdo que requieren tu atención.',
    icon: FaFileContract,
    color: 'blue',
  },
  {
    key: 'respuestasAcuerdos',
    title: 'Respuestas a propuestas',
    description: 'Respuestas a propuestas o acuerdos que has enviado.',
    icon: FaReply,
    color: 'green',
  },
  {
    key: 'resenasPendientes',
    title: 'Reseñas pendientes',
    description: 'Eventos finalizados en los que aún puedes dejar tu valoración.',
    icon: FaStar,
    color: 'orange',
  },
];

const LoginAlertsModal = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading } = useAuth();
  const [avisos, setAvisos] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const panelBg = useColorModeValue('gray.50', 'gray.700');
  const itemHoverBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.300');

  const isEligible =
    isAuthenticated &&
    (user?.userType === 'BANDA' || user?.userType === 'COFRADIA');

  const totalCount = useMemo(() => {
    if (!avisos) return 0;
    return (
      (avisos.nuevosChats?.length || 0) +
      (avisos.propuestasAcuerdos?.length || 0) +
      (avisos.respuestasAcuerdos?.length || 0) +
      (avisos.resenasPendientes?.length || 0)
    );
  }, [avisos]);

  const loadAvisos = useCallback(async () => {
    if (!isEligible || !hasPendingLoginAlerts()) return;
    setIsLoading(true);
    try {
      const response = await notificacionesAPI.getLoginAvisos();
      const data = response.data || {};
      const count =
        (data.nuevosChats?.length || 0) +
        (data.propuestasAcuerdos?.length || 0) +
        (data.respuestasAcuerdos?.length || 0) +
        (data.resenasPendientes?.length || 0);
      if (count > 0) {
        setAvisos(data);
        setIsOpen(true);
      } else {
        clearLoginAlertsPending();
      }
    } catch (error) {
      console.error('Error cargando avisos de login:', error);
      clearLoginAlertsPending();
    } finally {
      setIsLoading(false);
    }
  }, [isEligible]);

  useEffect(() => {
    if (loading || !isEligible) return;
    // Tras login, el flag de sessionStorage puede fijarse en el mismo ciclo que setUser.
    // Reintentamos en el siguiente tick por si el primer render aún no lo veía.
    const t = window.setTimeout(() => {
      loadAvisos();
    }, 0);
    return () => window.clearTimeout(t);
  }, [loading, isEligible, loadAvisos]);

  const handleClose = () => {
    setIsOpen(false);
    clearLoginAlertsPending();
  };

  const handleItemClick = async (item) => {
    if (item?.id) {
      try {
        await notificacionesAPI.dismissLoginAviso(item.id);
      } catch (error) {
        console.error('Error descartando aviso de login:', error);
      }
    }
    handleClose();
    if (item?.link) navigate(item.link);
  };

  if (!isOpen || !avisos || totalCount === 0) return null;

  return (
    <Dialog.Root open={isOpen} placement='center' size='lg' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        handleClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content mx={4}>
            <Dialog.Header pb={2}>
              <VStack align="start" gap={1}>
                <Text fontSize="lg">Tienes novedades pendientes</Text>
                <Text fontSize="sm" fontWeight="normal" color={mutedColor}>
                  Resumen de actividad desde tu última visita
                </Text>
              </VStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <VStack align="stretch" gap={5}>
                {SECTIONS.map((section) => {
                  const items = avisos[section.key] || [];
                  if (!items.length) return null;
                  return (
                    <Box key={section.key}>
                      <HStack gap={2} mb={2}>
                        <Icon color={`${section.color}.500`} asChild><section.icon /></Icon>
                        <Text fontWeight="semibold">{section.title}</Text>
                      </HStack>
                      <Text fontSize="sm" color={mutedColor} mb={3}>
                        {section.description}
                      </Text>
                      <VStack align="stretch" gap={2}>
                        {items.map((item) => (
                          <Box
                            key={item.id}
                            p={3}
                            rounded="lg"
                            bg={panelBg}
                            cursor={item.link ? 'pointer' : 'default'}
                            _hover={item.link ? { bg: itemHoverBg } : undefined}
                            onClick={() => item.link && handleItemClick(item)}
                            transition="background 0.15s"
                          >
                            <Text fontSize="sm" fontWeight="medium">
                              {item.titulo}
                            </Text>
                            <Text fontSize="sm" color={mutedColor} mt={0.5}>
                              {item.mensaje}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  );
                })}
              </VStack>
            </Dialog.Body>
            <Separator />
            <Dialog.Footer>
              <Button colorPalette="purple" onClick={handleClose} loading={isLoading}>
                Entendido
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
};

export default LoginAlertsModal;
